"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { House, LayoutGrid, LogOut, Menu, Plus, Route } from "lucide-react";
import { api } from "@/lib/api";
import { useSession, useEmpresa } from "@/core/session";
import { useGateAssinatura } from "@/core/conta";
import { AssinaturaScreen } from "@/features/assinatura/assinatura-screen";
import { useOnline } from "@/lib/use-now";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { EncerrarViagemDialog } from "@/features/mdfe/encerrar-dialog";
import { useNav, type MRoute, type MTab } from "./nav";
import { MInicio } from "./screens/inicio";
import { MViagem, MViagens } from "./screens/viagens";
import { MCiot, MCte, MDocumentos, MMdfe } from "./screens/documentos";
import { MBusca, MMais, MNotificacoes } from "./screens/mais";
import { LISTAS, MLista } from "./screens/listas";
import { MDesk } from "./screens/desk";
import { openScreen, setExternalTabHost } from "@/core/tabs";
import { escreverUrl, rotaAtual } from "@/core/rotas";
import type { Params, ScreenKey } from "@/core/screen-meta";

type MScreenProps = { params: Params; routeKey: string; ativo: boolean };

/**
 * Decisão por tela: quem tem versão nativa mobile usa ela; cadastros simples viram lista em cartões;
 * o resto abre a tela do desktop hospedada (MDesk). A chave é a mesma do desktop e da URL.
 */
const NATIVAS: Partial<Record<ScreenKey, React.ComponentType<MScreenProps>>> = {
  inicio: MInicio,
  viagens: MViagens,
  viagem: MViagem,
  documentos: MDocumentos,
  cte: MCte,
  mdfe: MMdfe,
  ciot: MCiot,
  menu: MMais,
  notificacoes: MNotificacoes,
  busca: MBusca,
};

function TelaMobile({ screen, params, routeKey, ativo }: { screen: ScreenKey } & MScreenProps) {
  const Nativa = NATIVAS[screen];
  if (Nativa) return <Nativa params={params} routeKey={routeKey} ativo={ativo} />;
  if (screen in LISTAS) return <MLista params={{ ...params, tipo: screen }} />;
  return <MDesk screen={screen} params={params} routeKey={routeKey} ativo={ativo} />;
}

/** Entrada única de navegação no mobile (openScreen, links diretos, notificações). */
function abrirNoMobile(screen: ScreenKey, params: Params) {
  const nav = useNav.getState();
  switch (screen) {
    case "inicio":
      return nav.setTab("inicio");
    case "viagens":
      return nav.setTab("viagens", params.periodo === "hoje" ? { filtro: "hoje" } : Object.keys(params).length ? params : undefined);
    case "ctes":
      return nav.setTab("documentos", { tipo: "cte" });
    case "mdfes":
      return nav.setTab("documentos", { tipo: "mdfe" });
    case "ciots":
      return nav.setTab("documentos", { tipo: "ciot" });
    case "documentos":
      return nav.setTab("documentos", { tipo: params.tipo ?? "mdfe" });
    case "menu":
      return nav.setTab("mais");
    default:
      return nav.push(screen, params);
  }
}

const TABS: { id: MTab; label: string; icon: LucideIcon }[] = [
  { id: "inicio", label: "Início", icon: House },
  { id: "viagens", label: "Viagens", icon: Route },
  { id: "documentos", label: "Documentos", icon: LayoutGrid },
  { id: "mais", label: "Menu", icon: Menu },
];

/** App mobile: barra de abas inferior + pilha de telas por aba (estado preservado). */
export function MobileApp() {
  const router = useRouter();
  const qc = useQueryClient();
  const token = useSession((s) => s.token);
  const empresaId = useSession((s) => s.empresaId);
  const empresa = useEmpresa();
  const online = useOnline();
  const tab = useNav((s) => s.tab);
  const stacks = useNav((s) => s.stacks);
  const dir = useNav((s) => s.dir);
  const bloqueada = useGateAssinatura();

  React.useEffect(() => {
    if (!token) {
      qc.removeQueries();
      router.replace("/login");
    } else if (!empresaId) router.replace("/empresa");
  }, [token, empresaId, router, qc]);

  // Telas desktop reaproveitadas abrem/fecham na pilha mobile.
  React.useEffect(() => {
    setExternalTabHost({
      open: abrirNoMobile,
      close: () => useNav.getState().back(),
      replace: (_id, screen, params) => useNav.getState().replaceTop(screen, params),
    });
    return () => setExternalTabHost(null);
  }, []);

  // URL inicial (link direto, recarregar, atalho do manifesto, retorno do checkout) → pilha
  React.useEffect(() => {
    if (!token || !empresaId) return;
    const r = rotaAtual();
    if (!r) return;
    const nav = useNav.getState();
    const aba = r.screen === "ctes" ? "documentos" : r.screen === "mdfes" ? "documentos" : r.screen === "ciots" ? "documentos" : null;
    if (aba) nav.aplicarUrl("documentos", { tipo: r.screen.slice(0, -1) });
    else nav.aplicarUrl(r.screen, r.params);
    // Link direto para uma tela empilhada: deixa a raiz da aba no histórico, abaixo dela, para o Voltar
    // levar à raiz em vez de sair do app
    const { tab, stacks } = useNav.getState();
    const pilha = stacks[tab];
    const t = pilha[pilha.length - 1];
    if (pilha.length > 1) {
      escreverUrl(pilha[0].screen, pilha[0].params, "replace");
      escreverUrl(t.screen, t.params, "push");
    } else {
      escreverUrl(t.screen, t.params, "replace");
    }
  }, [token, empresaId]);

  // Botão voltar/avançar do sistema → reconcilia a pilha com a URL.
  React.useEffect(() => {
    const onPop = () => {
      const r = rotaAtual();
      if (r) useNav.getState().aplicarUrl(r.screen, r.params);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (!token || !empresa) {
    return (
      <div className="flex h-dvh items-center justify-center text-muted">
        <Spinner className="size-5" />
      </div>
    );
  }

  // Conta sem plano: só a tela de assinatura
  if (bloqueada) {
    const sair = async () => {
      await api("/auth/logout", { method: "POST" }).catch(() => null);
      useSession.getState().logout();
      router.replace("/login");
    };
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-app">
        <header className="flex h-12 shrink-0 items-center gap-2 bg-menubar px-3 pt-[env(safe-area-inset-top)] text-menubar-fg">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">Assinatura</span>
          <button type="button" onClick={sair} className="flex h-9 items-center gap-1.5 rounded-md px-2 text-sm text-menubar-muted hover:text-menubar-fg">
            <LogOut className="size-4" />
            Sair
          </button>
        </header>
        <AssinaturaScreen />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-app text-[15px] select-none">
      {empresa.ambiente === "homologacao" ? <div aria-hidden className="h-[3px] shrink-0 bg-env-homolog" /> : null}
      {!online ? <div className="shrink-0 bg-warning-soft px-4 py-1.5 pt-[max(6px,env(safe-area-inset-top))] text-center text-xs font-medium text-warning">Sem conexão · mostrando dados salvos</div> : null}

      <main className="relative min-h-0 flex-1">
        {(Object.keys(stacks) as MTab[]).map((t) =>
          stacks[t].map((r: MRoute, i) => {
            const topo = i === stacks[t].length - 1;
            const visivel = t === tab && topo;
            return (
              <div
                key={r.key}
                hidden={!visivel}
                className="absolute inset-0"
                style={visivel ? { animation: `${dir === "push" ? "m-push" : dir === "pop" ? "m-pop" : "m-fade"} 150ms ease-out` } : undefined}
              >
                <TelaMobile screen={r.screen} params={r.params} routeKey={r.key} ativo={visivel} />
              </div>
            );
          }),
        )}
      </main>

      {/* Barra de abas: 4 destinos + "Nova viagem" no centro, a ação mais usada do sistema a um toque */}
      <nav aria-label="Navegação principal" className="relative shrink-0 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur supports-[backdrop-filter]:bg-surface/85">
        <div className="grid h-[70px] grid-cols-5 items-end">
          {TABS.slice(0, 2).map((t) => (
            <TabButton key={t.id} {...t} ativo={tab === t.id} />
          ))}
          <button
            type="button"
            aria-label="Nova viagem"
            onClick={() => openScreen("nova-viagem")}
            className="group flex h-full flex-col items-center justify-end gap-1 pb-1.5 text-[11px] font-medium text-primary select-none"
          >
            <span className="inline-flex size-11 items-center justify-center rounded-full bg-primary text-primary-fg shadow-[0_4px_12px_-3px_color-mix(in_srgb,var(--primary)_70%,transparent)] transition-transform duration-100 group-active:scale-95">
              <Plus className="size-6" strokeWidth={2.4} />
            </span>
            Nova viagem
          </button>
          {TABS.slice(2).map((t) => (
            <TabButton key={t.id} {...t} ativo={tab === t.id} />
          ))}
        </div>
      </nav>

      <EncerrarViagemDialog />
    </div>
  );
}

function TabButton({ id, label, icon: Icon, ativo }: { id: MTab; label: string; icon: LucideIcon; ativo: boolean }) {
  return (
    <button
      type="button"
      aria-current={ativo ? "page" : undefined}
      onClick={() => useNav.getState().setTab(id)}
      className={cn("flex h-full flex-col items-center justify-end gap-1 pb-1.5 text-[11px] font-medium transition-colors duration-100 select-none", ativo ? "text-primary" : "text-muted active:text-fg")}
    >
      <span className={cn("inline-flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-100", ativo && "bg-primary-soft")}>
        <Icon className="size-[22px]" strokeWidth={ativo ? 2.2 : 1.8} />
      </span>
      {label}
    </button>
  );
}
